<template>
  <div
    ref="container"
    class="focus-point set-marker-container"
    @click="handleClick"
  >
    <div
      ref="pin"
      :style="pinStyle"
      :class="[
        `icon-${shape}`,
        `is-${size}`,
        `is-border-${borderColor}`,
        { 'is-dragging': isDragging }
      ]"
      class="focus-point-pin set-marker-pin"
      tabindex="0"
      @pointerdown="handlePointerDown"
      @keydown="handleKeyDown"
      @click.stop
    >
      <div v-if="isDragging" class="coord-tooltip">
        {{ currentCoordText }}
      </div>
    </div>
    <slot />
  </div>
</template>

<script>
export default {
  name: 'SetMarker',
  props: {
    value: {
      type: Object,
      default: () => ({
        x: 50,
        y: 50,
      }),
    },
    shape: {
      type: String,
      required: true,
      default: 'circle',
    },
    size: {
      type: String,
      required: true,
      default: 'medium',
    },
    borderColor: {
      type: String,
      required: true,
      default: 'white',
    },
  },
  data() {
    return {
      pin: null,
      targetOffset: null,
      targetDimensions: null,
      isDragging: false,
      hasDragged: false,
      dragOffsetX: 0,
      dragOffsetY: 0,
    };
  },
  computed: {
    pinStyle() {
      const { width, height } = this.pin || { width: 18, height: 18 };
      const { x, y } = this.value || {};
      const posX = typeof x === 'number' ? x : parseFloat(x) || 50;
      const posY = typeof y === 'number' ? y : parseFloat(y) || 50;

      if (this.targetOffset && this.targetDimensions && this.targetDimensions.width) {
        const leftPx = this.targetOffset.left + (this.targetDimensions.width * posX) / 100 - width / 2;
        const topPx = this.targetOffset.top + (this.targetDimensions.height * posY) / 100 - height / 2;
        return {
          left: `${leftPx}px`,
          top: `${topPx}px`,
          color: 'rgba(255, 0, 0, 0.6)',
        };
      }

      return {
        left: this.s(posX, width),
        top: this.s(posY, height),
        color: 'rgba(255, 0, 0, 0.6)',
      };
    },
    currentCoordText() {
      const { x, y } = this.value || {};
      const posX = typeof x === 'number' ? x : parseFloat(x) || 50;
      const posY = typeof y === 'number' ? y : parseFloat(y) || 50;
      return `${posX.toFixed(1)}%, ${posY.toFixed(1)}%`;
    },
  },
  watch: {
    size() {
      this.$nextTick(this.updateMetrics);
    },
    shape() {
      this.$nextTick(this.updateMetrics);
    },
  },
  mounted() {
    this.$nextTick(this.updateMetrics);
    window.addEventListener('resize', this.updateMetrics);

    const img = this.$el.querySelector('img');
    if (img) {
      if (img.complete) {
        this.$nextTick(this.updateMetrics);
      } else {
        img.addEventListener('load', this.updateMetrics);
      }
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateMetrics);
    const img = this.$el.querySelector('img');
    if (img) {
      img.removeEventListener('load', this.updateMetrics);
    }
    this.cleanupPointerEvents();
  },
  methods: {
    getTargetElement() {
      const el = this.$el;
      if (!el) return null;
      return el.querySelector('.marker-container') ||
             el.querySelector('.image-marker-container') ||
             el.querySelector('.el-image') ||
             el.querySelector('img') ||
             this.$refs.container ||
             el;
    },
    updateMetrics() {
      if (this.$refs.pin) {
        this.pin = this.$refs.pin.getBoundingClientRect();
      }
      const target = this.getTargetElement();
      if (target && this.$el) {
        const targetRect = target.getBoundingClientRect();
        const containerRect = this.$el.getBoundingClientRect();
        this.targetOffset = {
          left: targetRect.left - containerRect.left,
          top: targetRect.top - containerRect.top,
        };
        this.targetDimensions = {
          width: targetRect.width,
          height: targetRect.height,
        };
      }
    },
    s(w, mw) {
      return `calc(${w}% - ${mw / 2}px)`;
    },
    handlePointerDown(event) {
      if (event.button !== 0 && event.pointerType === 'mouse') return;

      event.preventDefault();
      event.stopPropagation();

      this.updateMetrics();
      const pinRect = this.$refs.pin ? this.$refs.pin.getBoundingClientRect() : null;
      if (pinRect) {
        const pinCenterX = pinRect.left + pinRect.width / 2;
        const pinCenterY = pinRect.top + pinRect.height / 2;
        this.dragOffsetX = event.clientX - pinCenterX;
        this.dragOffsetY = event.clientY - pinCenterY;
      } else {
        this.dragOffsetX = 0;
        this.dragOffsetY = 0;
      }

      this.isDragging = true;
      this.hasDragged = false;

      if (event.target && event.target.setPointerCapture && event.pointerId !== undefined) {
        try {
          event.target.setPointerCapture(event.pointerId);
        } catch (e) {
          // ignore
        }
      }

      window.addEventListener('pointermove', this.handlePointerMove, { passive: false });
      window.addEventListener('pointerup', this.handlePointerUp);
      window.addEventListener('pointercancel', this.handlePointerUp);
    },
    handlePointerMove(event) {
      if (!this.isDragging) return;
      event.preventDefault();
      this.hasDragged = true;
      this.updatePosition(event.clientX - this.dragOffsetX, event.clientY - this.dragOffsetY);
    },
    handlePointerUp(event) {
      if (!this.isDragging) return;
      this.isDragging = false;

      this.cleanupPointerEvents();

      if (this.$refs.pin && event && event.pointerId !== undefined && this.$refs.pin.releasePointerCapture) {
        try {
          this.$refs.pin.releasePointerCapture(event.pointerId);
        } catch (e) {
          // ignore
        }
      }

      setTimeout(() => {
        this.hasDragged = false;
      }, 50);
    },
    cleanupPointerEvents() {
      window.removeEventListener('pointermove', this.handlePointerMove);
      window.removeEventListener('pointerup', this.handlePointerUp);
      window.removeEventListener('pointercancel', this.handlePointerUp);
    },
    handleClick(event) {
      if (this.hasDragged) {
        return;
      }
      this.updateMetrics();
      this.updatePosition(event.clientX, event.clientY);
    },
    handleKeyDown(event) {
      const step = event.shiftKey ? 1.0 : 0.1;
      let deltaX = 0;
      let deltaY = 0;

      if (event.key === 'ArrowLeft') deltaX = -step;
      else if (event.key === 'ArrowRight') deltaX = step;
      else if (event.key === 'ArrowUp') deltaY = -step;
      else if (event.key === 'ArrowDown') deltaY = step;
      else return;

      event.preventDefault();

      const { x, y } = this.value || {};
      const posX = typeof x === 'number' ? x : parseFloat(x) || 50;
      const posY = typeof y === 'number' ? y : parseFloat(y) || 50;

      const newX = Math.round(Math.max(0, Math.min(100, posX + deltaX)) * 10) / 10;
      const newY = Math.round(Math.max(0, Math.min(100, posY + deltaY)) * 10) / 10;

      this.$emit('input', {
        ...this.value,
        x: newX,
        y: newY,
      });
    },
    updatePosition(clientX, clientY) {
      const target = this.getTargetElement();
      if (!target) return;

      const { left, width, top, height } = target.getBoundingClientRect();
      if (!width || !height) return;

      const rawX = ((clientX - left) / width) * 100;
      const rawY = ((clientY - top) / height) * 100;

      const clampedX = Math.max(0, Math.min(100, rawX));
      const clampedY = Math.max(0, Math.min(100, rawY));

      const precisionX = Math.round(clampedX * 10) / 10;
      const precisionY = Math.round(clampedY * 10) / 10;

      this.$emit('input', {
        ...this.value,
        x: precisionX,
        y: precisionY,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.set-marker-container {
  display: inline-block;
  position: relative;
  vertical-align: top;
  user-select: none;
  max-width: 100%;
}

.set-marker-pin {
  z-index: 99 !important;
  pointer-events: auto !important;
  cursor: grab;
  touch-action: none;
  transition: all 0.15s ease-out;

  &:focus {
    outline: none;
  }

  &:hover {
    filter: brightness(1.2) drop-shadow(0 0 5px rgba(255, 0, 0, 0.7));
  }

  &.is-dragging {
    cursor: grabbing !important;
    transition: none !important;
    transform: scale(1.25);
    filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.5));
    z-index: 100 !important;
  }
}

.coord-tooltip {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: rgba(30, 30, 30, 0.85);
  color: #fff;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, monospace;
  white-space: nowrap;
  pointer-events: none;
  z-index: 101;
  line-height: 1.2;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}
</style>
